/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { SparkLineCH } from 'components/sparkLineCH'
import { SparkLineUSA } from 'components/sparkLineUSA'
import { SparkLineChina } from 'components/sparkLineChina'
import { SparkLineMarokko } from 'components/sparkLineMarokko'
import { SparkLineNorway } from 'components/sparkLineNorway'
import { SparkLineRussia } from 'components/sparkLineRussia'
import { SparkLineSaudiaArabia } from 'components/sparkLineSaudiaArabia'
import { SparkLineJapan } from 'components/sparkLineJapan'
import { SparkLineEU } from 'components/sparkLineEU'
import { SparkLineIndia } from 'components/sparkLineIndia'
import { SparkLineGlobal } from 'components/sparkLineGlobal'

import { CountryList } from 'components/list'
import { Dashboard } from 'components/dashboard'

// TODO Set the correct article ID here
const ARTICLE_ID = '501721772488'

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical   segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Welche Länder beim Klimaschutz auf Kurs sind</h1>
            <h2>
              Die Ambitionen der Teilnehmerstaaten an der Konferenz in Glasgow:
              Viele Klimaziele - und warum sie trotzdem nicht reichen.
            </h2>
          </div>
        </div>
      </header>

      {/* TODO And probably remove the following segment and replace it with
       your own content */}
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Martin Läubli, Patrick Vögeli und Marc Brupbacher
            </div>
            <div className='ta updated'>Aktualisiert am 28. Oktober 2021</div>
          </div>
        </div>
        <div className='ui container'>
          <h3 className='graphTitle'>
            Prognose für globale Erderwärmung bis 2100
          </h3>
          <div className='subtitle'>
            Treibhausgasemissionen in Gigatonnen CO₂ pro Jahr. Breite der Kurve
            gibt die Ungenauigkeit an.
          </div>
          <div style={{ marginBottom: '1.75rem' }}>
            <SparkLineGlobal />
          </div>
        </div>
        <div className='ui text container'>
          <div className='ta text'>
            <p>
              Es war ein historischer Moment, als die internationale
              Staatengemeinschaft 2015 das Pariser Klimaabkommen unterschrieb.
              Es ist wohl der ehrgeizigste Umweltvertrag, der auf der Ebene der
              UNO abgeschlossen wurde. Im Gegensatz zum ersten Klimavertrag, dem
              Kyoto-Protokoll, sind nun alle Länder der Welt im Kampf gegen den
              Klimawandel eingebunden. Das Abkommen gibt zwar keine
              verbindlichen Reduktionsziele für die Treibhausgase vor, dafür
              zwei Klimaziele: Die Erwärmung der Erde gegenüber dem
              vorindustriellen Niveau muss deutlich unter 2 Grad bleiben, und es
              müssen alle Anstrengungen unternommen werden, dass sie sich nicht
              mehr als 1,5 Grad erwärmt. Der diesen Sommer veröffentlichte neue
              Bericht des Weltklimarates IPCC konnte keine Entwarnung geben. Im
              Gegenteil. «Der Klimazustand hat sich rasch weiter verändert, und
              das Zeitfenster, um die Ziele des Pariser Abkommens zu erreichen,
              geht allmählich zu», sagt Erich Fischer, Klimaforscher an der ETH
              Zürich und einer der leitenden IPCC-Autoren.
            </p>
            <p>
              Neu ist auch, dass die Industriestaaten, die historisch betrachtet
              bisher am meisten CO₂ ausgestossen haben, grössere Anstrengungen
              unternehmen müssen als die armen Entwicklungsländer. Die einzelnen
              Staaten haben seither viele Versprechungen gemacht und Klimaziele
              festgelegt.
            </p>
            <p>
              In den letzten sechs Jahren hat sich technisch einiges verändert.
              War die Produktion von Sonnen- und Windenergie vor fünf Jahren im
              Vergleich zu Kohle und Erdöl noch viel teurer, so sind diese
              erneuerbaren Energiequellen vielerorts billiger und auf dem Markt
              konkurrenzfähig. Trotzdem ist die Welt noch lange nicht auf dem
              Pfad, den das Pariser Abkommen vorgibt.
            </p>
            <p>
              Würde man mit den heutigen Klimaschutzmassnahmen bis 2030
              weitermachen wie bisher, so würde sich die Erde bis Ende des
              Jahrhunderts um knapp 3 Grad erwärmen. Rechnet man die
              Versprechungen und Klimaziele der einzelnen Staaten bis 2030 dazu,
              dann käme man auf etwa 2,6 Grad. Inzwischen haben mehr als hundert
              Staaten, die für rund 63 Prozent der Emissionen verantwortlich
              sind, sogar ein Langzeitziel vorgelegt, nämlich bis 2050
              CO₂-neutral zu werden. Das heisst: Die Emissionen sinken praktisch
              auf null; was noch emittiert wird, muss kompensiert werden, indem
              man CO₂ aus der Atmosphäre entfernt. Sei es technisch oder
              natürlich zum Beispiel durch Aufforstung.
            </p>
          </div>
        </div>

        <div className='ui container'>
          <Dashboard />
        </div>

        <div className='ui text container'>
          <div className='ta text'>
            <p>
              Diese Einschätzungen stammen von einem internationalen Konsortium
              verschiedener Klimainstitute. Es bietet seit 2009 das Instrument
              Climate Action Tracker (CAT) an, um die nationalen Klimaprogramme
              der einzelnen Staaten, die regelmässig gemäss Klimaabkommen
              verlangt werden, untereinander zu vergleichen.
            </p>
            <p>
              Die Wissenschaftler analysieren dabei nicht nur die Emissionsdaten
              der einzelnen Staaten, sondern modellieren auch
              Zukunftsprojektionen, die anhand der aktuellen politischen
              Massnahmen, Versprechungen und Langzeitzielsetzungen entstehen.
              Zudem berücksichtigen sie den Passus des Klimavertrags, der auf
              einen fairen Beitrag zum Klimaschutz pocht, abhängig davon, wie
              reich und fortschrittlich das einzelne Land ist und welche
              historische Verantwortung es bezüglich der Emissionen trägt
            </p>
            <p>
              Um dies anschaulich zu machen, werten die Forscher die Ambitionen
              jedes einzelnen Staates, indem sie aufzeigen, wie sich die Erde
              erwärmen würde, wenn alle Staaten gleich ambitioniert wären. Diese
              Berechnungen sind selbstverständlich mit grossen Unsicherheiten
              verbunden, weil viele Annahmen in den Modellierungen gemacht
              werden müssen. Aber relativ betrachtet, geben sie ein
              realistisches Abbild, wie es um den internationalen Klimaschutz
              steht. Die Auswahl der folgenden Grafiken beruhen auf den Daten
              des Climate Action Tracker.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }} />

        <div className='ui text container'>
          <div className='contentWrapper' style={{ marginTop: '3rem' }}>
            <div className='arrowWrapper'>
              <img
                src={require('../imgs/icon_arrow.svg')}
                alt='arrow left icon'
              />
            </div>
            <div className='arrowText'>
              Wenn alle Länder die gleichen Klimaambitionen wie Argentinien
              hätten, erwärmte sich die Welt bis 2100 um 4&nbsp;Grad.
            </div>
          </div>
          <div className='listWrapper'>
            <CountryList />
          </div>
        </div>
      </div>

      {/* TODO Add your credits in this component */}
      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie verständlich waren die Grafiken?'
        ]}
        credits={[
          ['Text', 'Martin Läubli'],
          ['Digitale Umsetzung', 'Patrick Vögeli und Marc Brupbacher'],
          [
            'Quellen',
            `
            CO₂-Emissionen Länderprognosen:<br><a href="https://climateactiontracker.org/" target="_blank">The Climate Action Tracker</a>.
            <br>
            CO₂-Emissionen pro Kopf:<br><a href="https://ourworldindata.org/co2-emissions" target="_blank">Our World in Data</a>
            `
          ]
        ]}
      />
    </div>
  )
}
